// Bringing the assessor back to an evidence point (CONDITION-REPORTS.md
// "Same-view photos — phone test, 13 Oct 2026"): how far away they stand and
// which way to turn. Outdoors GPS and bearing get within a few metres and
// degrees; the overlaid reference photo does the final alignment by eye.
// "On the point" uses the reported accuracy, so indoor readings don't show
// false misses: within max(5 m, GPS accuracy) and 10°. Pure.

export interface Fix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

const EARTH_RADIUS_M = 6_371_000;
const ON_POINT_MIN_M = 5;
const ON_POINT_DEGREES = 10;
const FULL_TURN = 360;
const HALF_TURN = 180;

const radians = (degrees: number) => (degrees * Math.PI) / HALF_TURN;

/** Metres between two positions (haversine). */
export function distanceMetres(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const dLat = radians(b.latitude - a.latitude);
  const dLon = radians(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** Degrees to turn from `heading` to `target`: positive clockwise (right), negative left, within ±180. */
export function turnDegrees(heading: number, target: number): number {
  const d = (((target - heading) % FULL_TURN) + FULL_TURN) % FULL_TURN;
  return d > HALF_TURN ? d - FULL_TURN : d;
}

export interface Guidance {
  /** Metres from the point; null without a fix on either side. */
  metres: number | null;
  /** Degrees to turn (positive right); null without a heading on either side. */
  turn: number | null;
  onPoint: boolean;
}

/** Where the assessor stands and faces against the point's. A missing reading leaves its part null and the point unconfirmed. */
export function guidance(here: Fix | null, heading: number | null, point: { latitude: number | null; longitude: number | null; bearing: number | null }): Guidance {
  const metres = here && point.latitude !== null && point.longitude !== null ? distanceMetres(here, { latitude: point.latitude, longitude: point.longitude }) : null;
  const turn = heading !== null && point.bearing !== null ? turnDegrees(heading, point.bearing) : null;
  const near = metres !== null && here !== null && metres <= Math.max(ON_POINT_MIN_M, here.accuracy);
  const facing = turn !== null && Math.abs(turn) <= ON_POINT_DEGREES;
  return { metres, turn, onPoint: near && facing };
}

/** "4 m away · turn 12° right", or what's missing. */
export function guidanceText(g: Guidance): string {
  const where = g.metres === null ? 'No position yet' : `${Math.round(g.metres)} m away`;
  const turn = g.turn === null ? 'no compass' : Math.abs(g.turn) <= ON_POINT_DEGREES ? 'facing it' : `turn ${Math.round(Math.abs(g.turn))}° ${g.turn > 0 ? 'right' : 'left'}`;
  return `${where} · ${turn}`;
}

const sameName = (a: unknown, b: unknown) => typeof a === 'string' && typeof b === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase() && a.trim() !== '';

/**
 * The centre's evidence points that are this area's: on its register entry
 * when it has one; only an area not in the register yet (a later visit's job)
 * matches a register area of the same name (#576 review: two register areas
 * can share a name). The rest are the centre's others.
 */
export function pointsOfArea<T extends { registerAreaId: string; areaName: string }>(points: readonly T[], area: { registerId: unknown; name: unknown }): { mine: T[]; others: T[] } {
  const registerId = typeof area.registerId === 'string' && area.registerId ? area.registerId.toLowerCase() : null;
  const isMine = (p: T) => (registerId ? p.registerAreaId.toLowerCase() === registerId : sameName(p.areaName, area.name));
  return { mine: points.filter(isMine), others: points.filter((p) => !isMine(p)) };
}
